import React, { useEffect, useState } from 'react';

const API_BASE = '/api';

interface FlatCategory {
  id: string;
  label: string;
}

interface UploadModalProps {
  mode: 'new' | 'replace';
  categoryId: string;
  categories?: FlatCategory[]; // only needed for 'new' mode
  documentId?: string;
  documentTitle?: string;
  onClose: () => void;
  onSuccess: () => void;
}

function UploadModal({ mode, categoryId, categories, documentId, documentTitle, onClose, onSuccess }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateInfo, setDuplicateInfo] = useState<{ existingDocumentId: string; message: string } | null>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<{ message: string; existingDocumentId: string } | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [editedTitle, setEditedTitle] = useState(documentTitle || '');
  const [fileChecksum, setFileChecksum] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);
  const lastCheckedTitle = React.useRef<string>('');
  const [previewCode, setPreviewCode] = useState<string | null>(null);
  const [indexPrefix, setIndexPrefix] = useState('');
  const [indexSuffix, setIndexSuffix] = useState('');
  const documentIndex = `${indexPrefix}${indexSuffix}`;
  const hasIndex = indexPrefix.trim().length > 0;
  const fullTitle = documentName.trim()
    ? hasIndex
      ? `${documentIndex} - ${documentName.trim()}`
      : documentName.trim()
    : '';
  const [versionNumber, setVersionNumber] = useState('');

  useEffect(() => {
    if (mode !== 'replace' || !documentId) return;

    fetch(`${API_BASE}/documents/${documentId}/versions`, { credentials: 'include' })
      .then((res) => res.json())
      .then((versions) => {
        const currentMax = Math.max(...versions.map((v: any) => parseFloat(v.version_number)), 0);
        setVersionNumber((currentMax + 1).toString());
      })
      .catch(() => setVersionNumber(''));
  }, [documentId, mode]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    while (checkingDuplicate) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (!file) {
      setError('Please choose a file.');
      return;
    }

    if (mode === 'replace' && !changeNotes.trim()) {
      setError('Please describe what changed.');
      return;
    }

    if (mode === 'replace' && !versionNumber.trim()) {
      setError('Please enter a version number.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setDuplicateInfo(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let url: string;
      if (mode === 'new') {
        if (!documentName.trim()) {
          setError('Please enter a document name.');
          setSubmitting(false);
          return;
        }
        if (!selectedCategoryId) {
          setError('Please choose a category.');
          setSubmitting(false);
          return;
        }
        if (!indexSuffix.trim()) {
          setError('Please complete the document index.');
          setSubmitting(false);
          return;
        }
        formData.append('title', fullTitle);
        formData.append('category_id', selectedCategoryId);
        formData.append('document_index', documentIndex);
        url = `${API_BASE}/documents`;
      } else {
        formData.append('change_notes', changeNotes);
        formData.append('version_number', versionNumber);
        if (editedTitle.trim() && editedTitle !== documentTitle) {
          formData.append('title', editedTitle.trim());
        }
        url = `${API_BASE}/documents/${documentId}/versions`;
      }

      const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });

      if (res.status === 409) {
        const body = await res.json();
        if (body.error && (body.error.includes("Document index") || body.error.includes("Version"))) {
          setError(body.error);
          setSubmitting(false);
          return;
        }
        setDuplicateInfo({ existingDocumentId: body.existing_document_id, message: body.message });
        setSubmitting(false);
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setEditedTitle(documentTitle || '');
  }, [documentTitle]);

  const runDuplicateCheck = async (checksum: string | undefined, titleToCheck: string | undefined) => {
    if (!categoryId) return;
    try {
      const res = await fetch(`${API_BASE}/documents/check-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checksum, title: titleToCheck, category_id: categoryId }),
      });
      const data = await res.json();
      if (data.duplicate) {
        setDuplicateCheck({ message: data.message, existingDocumentId: data.existing_document_id });
      } else {
        setDuplicateCheck(null);
      }
    } catch {
      // fail silently — server-side check on submit still catches it
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setDuplicateCheck(null);
    setFileChecksum(null);

    if (!selected || !categoryId) return;

    setCheckingDuplicate(true);
    try {
      const checksum = await hashFile(selected);
      setFileChecksum(checksum);
      await runDuplicateCheck(checksum, mode === 'new' ? documentName : undefined);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  useEffect(() => {
    if (mode !== 'new' || !documentName.trim()) return;

    const timeout = setTimeout(() => {
      setCheckingDuplicate(true);
      runDuplicateCheck(fileChecksum || undefined, documentName).finally(() => setCheckingDuplicate(false));
    }, 500); // waits half a second after typing stops before checking

    return () => clearTimeout(timeout);
  }, [documentName, fileChecksum, mode]);

  const handleTitleBlur = async () => {
    if (mode !== 'new' || !documentName.trim()) return;
    if (documentName === lastCheckedTitle.current) return; // nothing's changed since we last checked, skip

    lastCheckedTitle.current = documentName;
    setCheckingDuplicate(true);
    await runDuplicateCheck(fileChecksum || undefined, documentName);
    setCheckingDuplicate(false);
  };

  const uploadAsVersionInstead = async () => {
    if (!duplicateInfo || !file) return;
    setDuplicateCheck(null); // Reset duplicate check info when uploading as new version
    setSubmitting(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('change_notes', 'Uploaded via duplicate-title resolution');

    const res = await fetch(`${API_BASE}/documents/${duplicateInfo.existingDocumentId}/versions`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });

    setSubmitting(false);
    if (res.ok) {
      onSuccess();
      onClose();
    } else {
      setError('Failed to upload as new version');
    }
  };

  async function hashFile(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  useEffect(() => {
    if (mode !== 'new' || !selectedCategoryId) {
      setPreviewCode(null);
      return;
    }

    fetch(`${API_BASE}/categories/${selectedCategoryId}/next-reference-code`)
      .then((res) => res.json())
      .then((data) => setPreviewCode(data.reference_code))
      .catch(() => setPreviewCode(null));
  }, [selectedCategoryId, mode]);

  useEffect(() => {
    if (mode !== 'new' || !selectedCategoryId) return;

    fetch(`${API_BASE}/categories/${selectedCategoryId}/prefix`)
      .then((res) => res.json())
      .then((data) => setIndexPrefix(data.prefix || ''))
      .catch(() => setIndexPrefix(''));
  }, [selectedCategoryId, mode]);

  const INDEX_SUFFIX_PATTERN = /^\d{3}[a-zA-Z]?$/;
  const indexSuffixValid = INDEX_SUFFIX_PATTERN.test(indexSuffix);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{mode === 'new' ? 'Upload New Document' : `Update: ${documentTitle}`}</h2>

        <form onSubmit={handleSubmit}>
          {mode === 'new' && (
            <div className="form-field">
              <label>Category</label>
              <p className="modal-static-value">
                {categories?.find((c) => c.id === selectedCategoryId)?.label || '—'}
              </p>
            </div>
          )}

          {mode === 'new' && hasIndex && (
            <div className="form-field">
              <label>
                Document Index <span className="required-marker">*</span>
                <span className="field-tooltip" title="Must be 3 digits, e.g. 001. Add a letter suffix if needed, e.g. 001a or 001b.">
                  ⓘ
                </span>
              </label>
              <div className="index-input-group">
                <span className="index-prefix-lock">{indexPrefix}</span>
                <input
                  type="text"
                  className="invite-input index-suffix-input"
                  value={indexSuffix}
                  onChange={(e) => setIndexSuffix(e.target.value)}
                  placeholder="001"
                  maxLength={4}
                />
              </div>
              {indexSuffix && !indexSuffixValid && (
                <p className="error">Must be 3 digits (e.g. 001), optionally followed by a letter (e.g. 001a).</p>
              )}
            </div>
          )}

          {mode === 'new' && (
            <div className="form-field">
              <label>Document Name <span className="required-marker">*</span></label>
              <input
                type="text"
                className="invite-input"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder="e.g. Manual Handling Policy"
              />
            </div>
          )}

          {mode === 'new' && (
            <div className="form-field">
              <label>Title</label>
              <p className="modal-static-value">
                {fullTitle || <span className="modal-static-value-muted">Will appear here once completed</span>}
              </p>
            </div>
          )}

          {mode === 'replace' && (
            <div className="form-field">
              <label>Display Name</label>
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
              />
            </div>
          )}

          {mode === 'replace' && (
            <div className="form-field">
              <label>What changed? <span className="required-marker">*</span></label>
              <input
                type="text"
                value={changeNotes}
                onChange={(e) => setChangeNotes(e.target.value)}
                placeholder="e.g. Updated section 3 wording"
              />
            </div>
          )}

          {mode === 'replace' && (
            <div className="form-field">
              <label>Version Number <span className="required-marker">*</span></label>
              <input
                type="text"
                className="invite-input"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
                placeholder="e.g. 2 or 1.1"
              />
            </div>
          )}

          <div className="form-field">
            <label>File</label>
            <label className="file-upload-button">
              Choose File
              <input
                type="file"
                className="file-upload-input"
                onChange={handleFileChange}
              />
            </label>
            {file && (
              <span className="file-upload-name">
                {file.name}
                <button
                  type="button"
                  className="file-remove-button"
                  onClick={() => {
                    setFile(null);
                    setFileChecksum(null);
                    setDuplicateCheck(null);
                  }}
                  aria-label="Remove file"
                >
                  &times;
                </button>
              </span>
            )}
          </div>

          {checkingDuplicate && <p>Checking for duplicates...</p>}
          {duplicateCheck && <p className="error">{duplicateCheck.message}</p>}
          {error && <p className="error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                submitting ||
                checkingDuplicate ||
                !!duplicateCheck ||
                !file ||
                (mode === 'replace' && (!changeNotes.trim() || !editedTitle.trim() || !versionNumber.trim())) ||
                (mode === 'new' && (!documentName.trim() || !selectedCategoryId || (hasIndex && !indexSuffixValid)))
              }
            >
              {checkingDuplicate ? 'Checking...' : submitting ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UploadModal;